import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CommunityPostCard from "../components/CommunityPostCard";
import PostDetailSheet from "../components/PostDetailSheet";
import { Sparkles, Plus, RotateCw } from "lucide-react";

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts", { params: { limit: 50 } });
      setPosts(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPostChanged = (p) => {
    setPosts((arr) => arr.map((x) => (x.post_id === p.post_id ? { ...x, ...p } : x)));
  };

  const onDelete = (post_id) => setPosts((arr) => arr.filter((x) => x.post_id !== post_id));

  const updateCommentCount = (count) => {
    if (!active) return;
    setPosts((arr) => arr.map((x) => (x.post_id === active.post_id ? { ...x, comment_count: count } : x)));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-24" data-testid="community-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tighter inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-pink-300" /> Community
          </h1>
          <p className="text-sm text-zinc-400">Real meals, real people, real reviews.</p>
        </div>
        <button
          onClick={() => navigate("/create-post")}
          className="inline-flex items-center gap-1 h-11 px-4 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
          data-testid="community-create-btn"
        >
          <Plus className="w-4 h-4" /> Post
        </button>
      </div>

      {loading && (
        <div className="grid place-items-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="text-center py-16 text-zinc-400" data-testid="community-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-bold">No posts yet.</p>
          <p className="text-sm mt-1">Be the first to share something delicious.</p>
          {user && (
            <button
              onClick={() => navigate("/create-post")}
              className="mt-5 inline-flex items-center gap-1 h-11 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
              data-testid="community-create-btn-empty"
            >
              <Plus className="w-4 h-4" /> Create the first post
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {posts.map((p) => (
          <CommunityPostCard
            key={p.post_id}
            post={p}
            onChange={onPostChanged}
            onOpen={(post) => setActive(post)}
            onDelete={onDelete}
          />
        ))}
      </div>

      {!loading && posts.length > 0 && (
        <button
          onClick={load}
          className="mx-auto flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
          data-testid="community-refresh"
        >
          <RotateCw className="w-4 h-4" /> Refresh
        </button>
      )}

      <PostDetailSheet
        post={active}
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
        onCommentCountChange={updateCommentCount}
      />
    </div>
  );
}
